import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Play, Mail, Loader2, RefreshCw } from "lucide-react";

const MILESTONE_COLOR = {
  "H-60": "bg-blue-100 text-blue-800",
  "H-14": "bg-amber-100 text-amber-900",
  "H-1": "bg-red-100 text-red-800",
};

export default function RemindersTab() {
  const [log, setLog] = React.useState([]);
  const [running, setRunning] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reminders/log");
      setLog(r.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/reminders/run-now");
      toast.success(`Scan selesai — dicek ${r.data.checked}, terkirim ${r.data.sent}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menjalankan reminder");
    } finally { setRunning(false); }
  };

  return (
    <TabsContent value="reminders" data-testid="tab-reminders-content">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-primary">Manasik Reminders</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Otomatis jam 08:00 WIB setiap hari. Kirim email H-60, H-14, H-1 ke jamaah dengan status <b>Konfirmasi</b>.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border hover:bg-muted" data-testid="reload-reminders">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh
          </button>
          <button onClick={runNow} disabled={running} className="btn-primary" data-testid="run-reminders-now">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run Now
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left">
            <tr>
              <th className="p-4 font-medium">Waktu Kirim</th>
              <th className="p-4 font-medium">Milestone</th>
              <th className="p-4 font-medium">Channel</th>
              <th className="p-4 font-medium">Kontak</th>
              <th className="p-4 font-medium">Booking ID</th>
              <th className="p-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {log.length === 0 && (
              <tr><td colSpan="6" className="p-10 text-center text-muted-foreground">Belum ada reminder terkirim.</td></tr>
            )}
            {log.map((r, i) => {
              const channel = r.channel || "email";
              const isWa = channel === "whatsapp";
              const success = isWa ? r.wa_ok : Boolean(r.pilgrim_message_id);
              return (
                <tr key={`${r.booking_id}-${r.milestone}-${i}`} className="border-t border-border" data-testid={`reminder-row-${r.booking_id}-${r.milestone}`}>
                  <td className="p-4 text-muted-foreground">{new Date(r.sent_at).toLocaleString("id-ID")}</td>
                  <td className="p-4">
                    <span className={`text-xs font-medium uppercase tracking-widest rounded-full px-2.5 py-1 ${MILESTONE_COLOR[r.milestone] || "bg-gray-100"}`}>
                      {r.milestone}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs font-medium uppercase tracking-widest rounded-full px-2.5 py-1 ${isWa ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                      {isWa ? "WhatsApp" : "Email"}
                    </span>
                  </td>
                  <td className="p-4 flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 text-accent" />{isWa ? r.phone : r.email}
                  </td>
                  <td className="p-4 font-mono text-xs text-muted-foreground">{r.booking_id.slice(-14)}</td>
                  <td className="p-4">
                    {success ? (
                      <span className="text-xs text-emerald-700">✓ terkirim</span>
                    ) : (
                      <span className="text-xs text-amber-700" title={r.wa_error || "gagal"}>⚠ {r.wa_error?.slice(0, 30) || "gagal"}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </TabsContent>
  );
}
